import React, { useEffect, useId, useState } from 'react'
import Modal from '../../modal/modal.component';
import classes from '../styles.module.scss';
import { createCn } from '../../../helpers/utils';
import { useTranslation } from 'react-i18next';
import { UploadIcon, VideoIcon, XIcon } from '../../../assets';
import { Meet, Teams, Zoom } from '../../../assets/pages';
import { MeetingOption } from './sidebar.component';

interface IProps {
  show: boolean;
  handleClose: () => void;
  handleStart: (type: MeetingOption) => void;
}

const cn = createCn('meeting-modal', classes);

// «Начать встречу» ('start') убрана из списка: видеозвонка внутри платформы нет,
// несуществующий функционал не показываем. Вернуть при появлении фичи.
const primaryOptions: MeetingOption[] = ['file'];
const integrationOptions: MeetingOption[] = ['zoom', 'teams', 'meet'];

function OptionIcon({ type }: { type: MeetingOption }) {
  if (type === 'start') {
    return (
      <span className={cn('icon-plain', { green: true })}>
        <VideoIcon />
      </span>
    );
  }
  if (type === 'file') {
    return (
      <span className={cn('icon-plain', { blue: true })}>
        <UploadIcon />
      </span>
    );
  }
  const logos: Partial<Record<MeetingOption, React.ReactNode>> = {
    zoom: <Zoom />,
    teams: <Teams />,
    meet: <Meet />,
  };
  return (
    <div className={cn('logo-wrap')}>
      {logos[type]}
    </div>
  );
}

const MeetingModal: React.FC<IProps> = ({ show, handleClose, handleStart }) => {
  const { t } = useTranslation();
  const groupName = useId();
  const [selected, setSelected] = useState<MeetingOption | null>(null);

  useEffect(() => {
    if (!show) setSelected(null);
  }, [show]);

  const handleContinue = () => {
    if (!selected) return;
    handleStart(selected);
  };

  const header = (
    <div className={cn('header')}>
      <div className={cn('header-text')}>
        <h2 className={cn('title')}>{t('action.choose-option')}</h2>
        <p className={cn('subtitle')}>{t('meeting.choose-subtitle')}</p>
      </div>
      <button
        type="button"
        className={cn('close-btn')}
        aria-label={t('action.close')}
        onClick={handleClose}
      >
        <XIcon />
      </button>
    </div>
  );

  const renderCard = (option: MeetingOption) => {
    const inputId = `${groupName}-${option}`;
    const isSelected = selected === option;

    return (
      <label
        key={option}
        htmlFor={inputId}
        className={cn('card', { selected: isSelected })}
      >
        <input
          id={inputId}
          type="radio"
          name={groupName}
          value={option}
          checked={isSelected}
          onChange={() => setSelected(option)}
          className={cn('radio')}
        />
        <OptionIcon type={option} />
        <div className={cn('card-text')}>
          <span className={cn('card-title')}>{t(`meeting.${option}`)}</span>
          <span className={cn('card-desc')}>{t(`meeting.${option}-description`)}</span>
        </div>
      </label>
    );
  };

  return (
    <Modal
      header={header}
      show={show}
      handleClose={handleClose}
      hideClose
      animated
      style={{ padding: 0, borderRadius: '16px', minWidth: 'auto', maxWidth: '640px' }}
      footer={
        <div className={cn('footer')}>
          <button
            type="button"
            className={cn('continue-btn')}
            onClick={handleContinue}
            disabled={!selected}
          >
            {t('action.continue')}
          </button>
        </div>
      }
    >
      <div className={cn('body')}>
        <div className={cn('grid', { primary: true })}>
          {primaryOptions.map(renderCard)}
        </div>

        <div className={cn('divider')}>
          <hr />
          <span>{t('meeting.bot-divider')}</span>
          <hr />
        </div>

        <div className={cn('grid', { integrations: true })}>
          {integrationOptions.map(renderCard)}
        </div>
      </div>
    </Modal>
  );
};

export default MeetingModal;
